import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import MediaControlsUi from './media-controls-ui';
import Portal from '../general-components/portal';
import VideoHoverOptions from './video-hover-options';
import useMedia from '../../hooks/use-media';
import useShotMemos from '../../hooks/use-shot-memos';
import { videoGridClasses } from '../../types/constants';
import { MediaItem, PresentedMediaItem } from '../../stores/reducers/media';
import { GreenroomState as GS } from '../../stores/types/types';
import V_LAYOUTS from '../../utils/video-layout';
import { isAudio, isVideo } from '../../utils/utils';
import { GeneratedContent } from '../../stores/types/content';
import { STATIC_ASSET_URL } from '../../utils/content-templates';

interface FeedMediaControlProps {
    mediaItem: MediaItem;
    isForMainVideoEditingComponent?: boolean;
    feedPosition: number;
    audioOnly?: boolean;
    isEditingFeed: boolean;
    isForShotEditorModal?: boolean;
    showbuilderMediaItem?: PresentedMediaItem;
    updateShowbuilderMedia?: Function;
}

const FeedMediaControl: React.FC<FeedMediaControlProps> = ({
	mediaItem,
	isForMainVideoEditingComponent,
	feedPosition,
	audioOnly,
	isEditingFeed = false,
	showbuilderMediaItem,
	isForShotEditorModal = false,
	updateShowbuilderMedia = () => {},
}) => {
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const [isHovering, setIsHovering] = useState<boolean>(false);
	const mediaId = mediaItem.id;
	const globalIsDragging = useSelector(
		(state: GS) => state.UserInterfaceReducer.globalIsDragging,
	);
	const videoElRef = useRef<HTMLVideoElement>(null);
	const timeBubbleRef = useRef<HTMLInputElement>(null);
	const {
		currentShot,
	} = useShotMemos();
	const {
		shot_data: {
			forcedLayout,
		},
	} = currentShot;
	const {
		canEditOrControlMedia,
	} = useMedia();

	const isHiddenTwoUpShot = useMemo(
		(): boolean => feedPosition === 0
            && forcedLayout === V_LAYOUTS.TWO.FULL_TWO,
		[forcedLayout, feedPosition],
	);
	const presentedMediaItem = useMemo(
		() => presentedMedia
			.find((m) => m.mediaId === mediaId),
		[presentedMedia, mediaId],
	);
	const urlOfCurrentPage = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);
	const isDeck = useMemo(() => {
		if (!mediaItem || !mediaItem.num_pages) return false;
		return mediaItem.num_pages > 1;
	}, [mediaItem?.num_pages]);
	const currentPageIsVideo = useMemo(() => isVideo(urlOfCurrentPage), [urlOfCurrentPage]);
	const currentPageIsAudio = useMemo(() => isAudio(urlOfCurrentPage), [urlOfCurrentPage]);

	const mediaGeneratedContent = useMemo(() => {
		if (!generatedContent) {
			return undefined;
		}

		return generatedContent.find((t: GeneratedContent) => t.fkId === mediaId);
	}, [mediaId, generatedContent]);

	const mediaGeneratedContentTemplate = useMemo(() => {
		if (mediaGeneratedContent) {
			return contentTemplates.find((t) => t.id === mediaGeneratedContent.templateId);
		}

		if (mediaItem) {
			// non-edited media items image will match a template
			return contentTemplates.find((t) => t.videoUrl
				?.replace('ASSET_URL', STATIC_ASSET_URL ?? '') === mediaItem?.images);
		}
		return null;
	}, [mediaGeneratedContent, mediaItem]);

	return (<>
        {
            mediaItem
            && !globalIsDragging
            && canEditOrControlMedia(mediaItem, user, host, true, roomState)
            && <Portal
                targetId={isForMainVideoEditingComponent ? 'controls-container-editing' : 'controls-container-live'}
                className={classNames(
                	`media-controls-layer width-full height-full ${videoGridClasses[feedPosition]}`,
                	{
                		hover: isHovering,
                		'hidden-feed': isHiddenTwoUpShot,
                		'display-none': audioOnly,
                	},
                )}
            >

                <div
                    className="flex width-full"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    {(currentPageIsVideo || isDeck)
                        && <MediaControlsUi
                            isEditingFeed={isEditingFeed}
                            showbuilderMediaItem={showbuilderMediaItem}
                            mediaId={mediaId}
                            isForShotEditorModal={isForShotEditorModal}
                            host={host}
                            isDeck={isDeck}
                            timeBubbleRef={timeBubbleRef}
                            updateShowbuilderMedia={updateShowbuilderMedia}/>
                }
                   {mediaGeneratedContentTemplate && host && (
							<Portal targetId="video-hover-options-portal">
								<VideoHoverOptions
									mediaItem={mediaItem}
									generateContentTemplate={mediaGeneratedContentTemplate}
									className={classNames(
										'width-full',
										'height-full',
										videoGridClasses[feedPosition],
										{
											'hidden-feed': isHiddenTwoUpShot,
											'display-none': audioOnly,
										},
									)}
									isHovering={isHovering}
									videoRef={videoElRef.current}
								/>
							</Portal>
                   )}
                </div>
            </Portal>

        }
        </>
	);
};
interface FeedMediaControlsProps {
    isForMainVideoEditingComponent?: boolean;
}

export default function FeedMediaControls(props: FeedMediaControlsProps) {
	const { isForMainVideoEditingComponent } = props;
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const {
		currentShot,
	} = useShotMemos();

	const renderAllControls = () => currentShot.shot_data.shotFeeds.map((feed, index) => {
		const { media: mediaId, position: feedPosition, audioOnly } = feed;

		const mediaItem = media.find((m: MediaItem) => m.id === mediaId);
		if (!mediaItem) return null;
		return (
                <FeedMediaControl
                    key={mediaId}
                    mediaItem={mediaItem}
                    isForMainVideoEditingComponent={isForMainVideoEditingComponent}
                    feedPosition={feedPosition}
                    audioOnly={audioOnly}
                    isEditingFeed={false}
                    isForShotEditorModal={false}
                />
		);
	});

	return (
        <>
            {renderAllControls()}
        </>
	);
}
